import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Check, X, Pencil, RefreshCw, Sparkles, ExternalLink } from "lucide-react";
import {
  listGrants,
  grantsStats,
  updateGrant,
  setGrantReview,
  setGrantStatus,
  runGrantsExpirySweep,
  listResearchRuns,
  requestResearchRun,
  type GrantRow,
  type GrantFilters,
  type ResearchRun,
} from "@/lib/admin-grants.functions";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

export const Route = createFileRoute("/admin/grants")({
  head: () => ({
    meta: [
      { title: "Grants - Admin - DeckScore" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminGrantsPage,
});

type Tab = "queue" | "all" | "enrich";

function AdminGrantsPage() {
  const runListGrants = useServerFn(listGrants);
  const runStats = useServerFn(grantsStats);
  const runUpdate = useServerFn(updateGrant);
  const runReview = useServerFn(setGrantReview);
  const runStatus = useServerFn(setGrantStatus);
  const runExpiry = useServerFn(runGrantsExpirySweep);
  const runListRuns = useServerFn(listResearchRuns);
  const runRequestRun = useServerFn(requestResearchRun);

  const [tab, setTab] = useState<Tab>("queue");
  const [grants, setGrants] = useState<GrantRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<{ total: number; pending: number; approved: number; rejected: number; live: number; lastUpdate: string | null } | null>(null);
  const [runs, setRuns] = useState<ResearchRun[]>([]);
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const [runNote, setRunNote] = useState("");
  const [editing, setEditing] = useState<GrantRow | null>(null);

  // All-grants filters
  const [filters, setFilters] = useState<GrantFilters>({
    review_status: "all",
    status: "all",
    search: "",
  });

  async function refreshQueue() {
    setLoading(true);
    const { grants } = await runListGrants({ data: { review_status: "pending" } });
    setGrants(grants);
    setLoading(false);
  }
  async function refreshAll() {
    setLoading(true);
    const { grants } = await runListGrants({ data: filters });
    setGrants(grants);
    setLoading(false);
  }
  async function refreshStats() {
    const s = await runStats();
    setStats(s);
  }
  async function refreshRuns() {
    const { runs } = await runListRuns();
    setRuns(runs);
  }

  useEffect(() => {
    refreshStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tab === "queue") refreshQueue();
    else if (tab === "all") refreshAll();
    else if (tab === "enrich") refreshRuns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filters.review_status, filters.status, filters.search]);

  async function approve(g: GrantRow) {
    await runReview({ data: { id: g.id, review_status: "approved" } });
    toast.success(`Approved "${g.name}"`);
    await Promise.all([tab === "queue" ? refreshQueue() : refreshAll(), refreshStats()]);
  }
  async function reject(g: GrantRow) {
    await runReview({ data: { id: g.id, review_status: "rejected" } });
    toast.success(`Rejected "${g.name}"`);
    await Promise.all([tab === "queue" ? refreshQueue() : refreshAll(), refreshStats()]);
  }
  async function reapprove(g: GrantRow) {
    await runReview({ data: { id: g.id, review_status: "approved" } });
    toast.success(`Re-approved "${g.name}"`);
    await Promise.all([refreshAll(), refreshStats()]);
  }
  async function toggleOpenClosed(g: GrantRow) {
    const next = g.status === "open" ? "closed" : "open";
    await runStatus({ data: { id: g.id, status: next } });
    toast.success(`Set status: ${next}`);
    await refreshAll();
  }
  async function saveEdit(patch: {
    id: string;
    name: string;
    amount: string;
    closing_date: string;
    application_url: string;
    sector_focus: string;
    eligibility: string[];
  }) {
    await runUpdate({ data: patch });
    toast.success("Saved");
    setEditing(null);
    if (tab === "queue") await refreshQueue();
    else await refreshAll();
  }

  async function handleExpirySweep() {
    const res = await runExpiry();
    toast.success(`Sweep done - ${res.newly_expired} newly expired, ${res.newly_closed} newly closed`);
    await refreshStats();
  }
  async function submitRunRequest() {
    await runRequestRun({ data: { note: runNote } });
    toast.success("Research run requested");
    setRunNote("");
    setRunDialogOpen(false);
    await refreshRuns();
  }

  const pendingBadge = stats?.pending ?? 0;

  return (
    <>
      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="font-script text-accent text-[22px] leading-none">grants ops</p>
          <h1 className="font-display text-[28px] font-semibold mt-2">Grants</h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-line">
        <TabButton active={tab === "queue"} onClick={() => setTab("queue")}>
          Review queue
          {pendingBadge > 0 && (
            <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-accent text-accent-ink text-[11px] font-semibold">
              {pendingBadge}
            </span>
          )}
        </TabButton>
        <TabButton active={tab === "all"} onClick={() => setTab("all")}>All grants</TabButton>
        <TabButton active={tab === "enrich"} onClick={() => setTab("enrich")}>Database enrichment</TabButton>
      </div>

      {tab === "queue" && (
        <QueueView grants={grants} loading={loading} onApprove={approve} onReject={reject} onEdit={setEditing} />
      )}

      {tab === "all" && (
        <AllView
          grants={grants}
          loading={loading}
          filters={filters}
          setFilters={setFilters}
          onEdit={setEditing}
          onToggleStatus={toggleOpenClosed}
          onReapprove={reapprove}
          onReject={reject}
        />
      )}

      {tab === "enrich" && (
        <EnrichView
          stats={stats}
          runs={runs}
          onSweep={handleExpirySweep}
          onOpenRunDialog={() => setRunDialogOpen(true)}
        />
      )}

      {/* Edit dialog */}
      {editing && (
        <EditDialog grant={editing} onClose={() => setEditing(null)} onSave={saveEdit} />
      )}

      {/* AI run dialog */}
      <Dialog open={runDialogOpen} onOpenChange={setRunDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request AI enrichment run</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Optional note</Label>
            <Textarea
              value={runNote}
              onChange={(e) => setRunNote(e.target.value)}
              placeholder="e.g. focus on climate grants"
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRunDialogOpen(false)}>Cancel</Button>
            <Button onClick={submitRunRequest} className="bg-accent text-accent-ink hover:bg-accent-press">Request run</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2.5 text-[13.5px] font-medium border-b-2 -mb-px transition ${
        active ? "border-accent text-ink" : "border-transparent text-ink-secondary hover:text-ink"
      }`}
    >
      {children}
    </button>
  );
}

function QueueView({
  grants,
  loading,
  onApprove,
  onReject,
  onEdit,
}: {
  grants: GrantRow[];
  loading: boolean;
  onApprove: (g: GrantRow) => void;
  onReject: (g: GrantRow) => void;
  onEdit: (g: GrantRow) => void;
}) {
  if (loading) return <div className="text-ink-secondary">Loading…</div>;
  if (!grants.length) return <div className="text-ink-secondary">Nothing pending. 🎉</div>;
  return (
    <div className="grid gap-4">
      {grants.map((g) => (
        <div key={g.id} className="bg-surface border border-line rounded-2xl p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-display text-[18px] font-semibold">{g.name}</h3>
                {g.program_type && <Chip>{g.program_type.replace(/_/g, " ")}</Chip>}
                {g.country && <Chip>{g.country}</Chip>}
                {g.regional_focus && <Chip>{g.regional_focus}</Chip>}
              </div>
              {g.scheme && <div className="text-[13px] text-ink-secondary mt-1">{g.scheme}</div>}
              {g.host_organization && <div className="text-[12.5px] text-ink-tertiary mt-0.5">Host: {g.host_organization}</div>}
            </div>
            <div className="flex gap-2 shrink-0">
              <Button size="sm" variant="outline" onClick={() => onEdit(g)}><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
              <Button size="sm" variant="outline" onClick={() => onReject(g)}><X className="w-3.5 h-3.5 mr-1" />Reject</Button>
              <Button size="sm" className="bg-accent text-accent-ink hover:bg-accent-press" onClick={() => onApprove(g)}>
                <Check className="w-3.5 h-3.5 mr-1" />Approve
              </Button>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3 mt-4 text-[13px]">
            <Field label="Amount" value={g.amount} />
            <Field label="Closing" value={g.is_rolling ? "Rolling" : g.closing_date} />
            <LinkField label="Application URL" href={g.application_url} />
            <LinkField label="Source URL" href={g.source_url} />
          </div>

          {g.description && (
            <p className="mt-4 text-[13.5px] text-ink-secondary whitespace-pre-wrap">{g.description}</p>
          )}
          {g.eligibility && g.eligibility.length > 0 && (
            <div className="mt-3">
              <div className="text-[12px] text-ink-tertiary uppercase tracking-wider mb-1">Eligibility</div>
              <ul className="list-disc pl-5 text-[13px] text-ink-secondary space-y-0.5">
                {g.eligibility.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function AllView({
  grants,
  loading,
  filters,
  setFilters,
  onEdit,
  onToggleStatus,
  onReapprove,
  onReject,
}: {
  grants: GrantRow[];
  loading: boolean;
  filters: GrantFilters;
  setFilters: (u: (f: GrantFilters) => GrantFilters) => void;
  onEdit: (g: GrantRow) => void;
  onToggleStatus: (g: GrantRow) => void;
  onReapprove: (g: GrantRow) => void;
  onReject: (g: GrantRow) => void;
}) {
  return (
    <>
      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div className="flex flex-col gap-1">
          <Label className="text-[12px] text-ink-tertiary">Review</Label>
          <Select value={filters.review_status ?? "all"} onValueChange={(v) => setFilters((f) => ({ ...f, review_status: v as GrantFilters["review_status"] }))}>
            <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-[12px] text-ink-tertiary">Status</Label>
          <Select value={filters.status ?? "all"} onValueChange={(v) => setFilters((f) => ({ ...f, status: v as GrantFilters["status"] }))}>
            <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="upcoming">Upcoming</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
          <Label className="text-[12px] text-ink-tertiary">Search by name</Label>
          <Input
            value={filters.search ?? ""}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            placeholder="Search…"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-ink-secondary">Loading…</div>
      ) : !grants.length ? (
        <div className="text-ink-secondary">No grants match.</div>
      ) : (
        <div className="bg-surface border border-line rounded-2xl overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="text-left text-ink-tertiary text-[12px] uppercase tracking-wider border-b border-line">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Country</th>
                <th className="px-4 py-3">Closing</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Review</th>
                <th className="px-4 py-3">Updated</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {grants.map((g) => (
                <tr key={g.id} className="border-b border-line last:border-0">
                  <td className="px-4 py-3 font-medium">{g.name}</td>
                  <td className="px-4 py-3 text-ink-secondary">{g.program_type ?? "-"}</td>
                  <td className="px-4 py-3 text-ink-secondary">{g.country ?? "-"}</td>
                  <td className="px-4 py-3 text-ink-secondary">{g.is_rolling ? "Rolling" : g.closing_date ?? "-"}</td>
                  <td className="px-4 py-3"><StatusChip value={g.status} /></td>
                  <td className="px-4 py-3"><ReviewChip value={g.review_status} /></td>
                  <td className="px-4 py-3 text-ink-tertiary">{g.updated_at ? new Date(g.updated_at).toLocaleDateString() : "-"}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1.5 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => onEdit(g)}>Edit</Button>
                      {(g.status === "open" || g.status === "closed") && (
                        <Button size="sm" variant="outline" onClick={() => onToggleStatus(g)}>
                          {g.status === "open" ? "Close" : "Open"}
                        </Button>
                      )}
                      {g.review_status === "rejected" && (
                        <Button size="sm" className="bg-accent text-accent-ink hover:bg-accent-press" onClick={() => onReapprove(g)}>Re-approve</Button>
                      )}
                      {g.review_status === "approved" && (
                        <Button size="sm" variant="outline" onClick={() => onReject(g)}>Reject</Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function EnrichView({
  stats,
  runs,
  onSweep,
  onOpenRunDialog,
}: {
  stats: { total: number; pending: number; approved: number; rejected: number; live: number; lastUpdate: string | null } | null;
  runs: ResearchRun[];
  onSweep: () => void;
  onOpenRunDialog: () => void;
}) {
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <StatCard label="Total programs" value={stats?.total ?? "-"} />
        <StatCard label="Live" value={stats?.live ?? "-"} />
        <StatCard label="Pending review" value={stats?.pending ?? "-"} />
        <StatCard label="Rejected" value={stats?.rejected ?? "-"} />
        <StatCard label="Last data update" value={stats?.lastUpdate ? new Date(stats.lastUpdate).toLocaleString() : "-"} small />
      </div>

      <div className="flex flex-wrap gap-3 mb-3">
        <Button variant="outline" onClick={onSweep}>
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Run expiry sweep now
        </Button>
        <Button onClick={onOpenRunDialog} className="bg-accent text-accent-ink hover:bg-accent-press">
          <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Request AI enrichment run
        </Button>
      </div>

      <p className="text-[12.5px] text-ink-tertiary mb-6">
        The AI research agent runs daily at 7:30 AM (weekly deep-scan sources rotate by weekday) and picks up on-demand requests at each run. New finds land in the Review Queue as Pending.
      </p>

      <h3 className="font-display text-[16px] font-semibold mb-3">Recent runs</h3>
      {!runs.length ? (
        <div className="text-ink-secondary text-[13px]">No runs yet.</div>
      ) : (
        <div className="bg-surface border border-line rounded-2xl divide-y divide-line">
          {runs.map((r) => (
            <div key={r.id} className="p-4 flex items-start gap-3 flex-wrap">
              <RunStatusChip value={r.status} />
              <div className="min-w-0 flex-1">
                <div className="text-[13px]">
                  <span className="text-ink-tertiary">{r.scope ?? "-"}</span>
                  {r.note && <span className="text-ink-secondary"> · {r.note}</span>}
                </div>
                {r.summary && <div className="text-[12.5px] text-ink-secondary mt-1 whitespace-pre-wrap">{r.summary}</div>}
              </div>
              <div className="text-[12px] text-ink-tertiary shrink-0">
                {r.requested_at ? new Date(r.requested_at).toLocaleString() : "-"}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function EditDialog({
  grant,
  onClose,
  onSave,
}: {
  grant: GrantRow;
  onClose: () => void;
  onSave: (p: {
    id: string;
    name: string;
    amount: string;
    closing_date: string;
    application_url: string;
    sector_focus: string;
    eligibility: string[];
  }) => Promise<void>;
}) {
  const [name, setName] = useState(grant.name);
  const [amount, setAmount] = useState(grant.amount ?? "");
  const [closingDate, setClosingDate] = useState(grant.closing_date ?? "");
  const [applicationUrl, setApplicationUrl] = useState(grant.application_url ?? "");
  const [sectorFocus, setSectorFocus] = useState(grant.sector_focus ?? "");
  const [eligibilityText, setEligibilityText] = useState((grant.eligibility ?? []).join("\n"));
  const [saving, setSaving] = useState(false);

  async function submit() {
    setSaving(true);
    try {
      await onSave({
        id: grant.id,
        name,
        amount,
        closing_date: closingDate,
        application_url: applicationUrl,
        sector_focus: sectorFocus,
        eligibility: eligibilityText.split("\n").map((s) => s.trim()).filter(Boolean),
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit grant</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <div>
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Amount</Label>
              <Input value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
            <div>
              <Label>Closing date</Label>
              <Input type="date" value={closingDate ?? ""} onChange={(e) => setClosingDate(e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Application URL</Label>
            <Input value={applicationUrl} onChange={(e) => setApplicationUrl(e.target.value)} />
          </div>
          <div>
            <Label>Sector focus</Label>
            <Input value={sectorFocus} onChange={(e) => setSectorFocus(e.target.value)} />
          </div>
          <div>
            <Label>Eligibility (one item per line)</Label>
            <Textarea rows={5} value={eligibilityText} onChange={(e) => setEligibilityText(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-accent text-accent-ink hover:bg-accent-press">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value, small }: { label: string; value: React.ReactNode; small?: boolean }) {
  return (
    <div className="bg-surface border border-line rounded-2xl p-4">
      <div className="text-[11.5px] uppercase tracking-wider text-ink-tertiary">{label}</div>
      <div className={`mt-1 font-display font-semibold ${small ? "text-[15px]" : "text-[24px]"}`}>{value}</div>
    </div>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return <span className="inline-block px-2 py-0.5 rounded-full bg-surface-sunken text-[11.5px] text-ink-secondary">{children}</span>;
}

function StatusChip({ value }: { value: string | null }) {
  if (!value) return <span className="text-ink-tertiary">-</span>;
  const color = value === "open" ? "bg-[color:var(--color-accent-mint)] text-ink"
    : value === "expired" ? "bg-[color:var(--color-peach)] text-ink"
    : value === "closed" ? "bg-surface-sunken text-ink-secondary"
    : "bg-[color:var(--color-accent-lavender)] text-ink";
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[11.5px] ${color}`}>{value}</span>;
}
function ReviewChip({ value }: { value: string | null }) {
  if (!value) return <span className="text-ink-tertiary">-</span>;
  const color = value === "approved" ? "bg-[color:var(--color-accent-mint)] text-ink"
    : value === "rejected" ? "bg-red-100 text-red-800"
    : "bg-amber-100 text-amber-900";
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[11.5px] ${color}`}>{value}</span>;
}
function RunStatusChip({ value }: { value: string | null }) {
  const v = value ?? "requested";
  const map: Record<string, string> = {
    requested: "bg-amber-100 text-amber-900",
    processing: "bg-sky-100 text-sky-900",
    done: "bg-[color:var(--color-accent-mint)] text-ink",
    failed: "bg-red-100 text-red-800",
  };
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[11.5px] shrink-0 ${map[v] ?? "bg-surface-sunken text-ink-secondary"}`}>{v}</span>;
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11.5px] uppercase tracking-wider text-ink-tertiary">{label}</div>
      <div className="mt-0.5 text-ink">{value || "-"}</div>
    </div>
  );
}
function LinkField({ label, href }: { label: string; href: string | null }) {
  return (
    <div>
      <div className="text-[11.5px] uppercase tracking-wider text-ink-tertiary">{label}</div>
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className="mt-0.5 inline-flex items-center gap-1 text-accent hover:underline break-all">
          {href} <ExternalLink className="w-3 h-3 shrink-0" />
        </a>
      ) : <div className="mt-0.5 text-ink-tertiary">-</div>}
    </div>
  );
}
